import React from 'react';
import {
  FileText, DollarSign, Edit3, CheckCircle, Eye, Check, XCircle, SquareCheck,
  Send, Ban, FileX, X, Search, Filter, Square, MinusSquare, Banknote,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { statusConfig, getEffectiveStatus } from './invoicesConstants';
import { LocationLine } from '../../../components/LocationLine';

const StatCard = ({ icon: Icon, iconBg, iconColor, label, value, valueColor, ring }) => (
  <Card className={ring}>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 ${iconBg} rounded-lg`}><Icon className={`h-5 w-5 ${iconColor}`} /></div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className={`text-2xl font-bold ${valueColor || ''}`}>{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const InvoicesTab = ({
  invoices, filteredInvoices, clientOptions,
  pendingReview, totalOutstanding, totalPaid,
  searchQuery, setSearchQuery, statusFilter, setStatusFilter, clientFilter, setClientFilter,
  selectedIds, toggleSelect, toggleSelectAll, clearSelection,
  canBulkCancel, canBulkVoid, canBulkPay, canBulkRemind, processing,
  onViewDetail, onApprove, onReject, onApplyPayment,
  onOpenBulkPay, onOpenReminder, onOpenCancel, onOpenVoidBulk,
}) => (
  <>
    {/* Stats */}
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
      <StatCard icon={FileText} iconBg="bg-blue-100" iconColor="text-blue-600" label="Total Invoices" value={invoices.length} />
      <StatCard icon={DollarSign} iconBg="bg-yellow-100" iconColor="text-yellow-600" label="Outstanding" value={`$${totalOutstanding.toFixed(2)}`} />
      <StatCard icon={Edit3} iconBg="bg-purple-100" iconColor="text-purple-600" label="Pending Review" value={pendingReview.length} valueColor="text-purple-700" ring={pendingReview.length > 0 ? 'ring-2 ring-purple-400' : ''} />
      <StatCard icon={CheckCircle} iconBg="bg-green-100" iconColor="text-green-600" label="Total Paid" value={`$${totalPaid.toFixed(2)}`} valueColor="text-green-700" />
    </div>

    {/* Pending Adjustments Alert */}
    {pendingReview.length > 0 && (
      <Card className="border-purple-300 bg-purple-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <Edit3 className="h-5 w-5 text-purple-600" />
            <p className="font-semibold text-purple-800">Adjustment Requests Pending Review ({pendingReview.length})</p>
          </div>
          <div className="space-y-2">
            {pendingReview.slice(0, 3).map(inv => {
              const pending = (inv.adjustment_requests || []).find(a => a.status === 'pending');
              return (
                <div key={inv.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div>
                    <p className="font-medium">{inv.invoice_number} — {inv.client_name}</p>
                    {pending && <p className="text-sm text-gray-500">${pending.original_amount?.toFixed(2)} → ${pending.requested_amount?.toFixed(2)} ({pending.reason?.substring(0, 50)}...)</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => onViewDetail(inv)}><Eye className="h-4 w-4 mr-1" />Review</Button>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => onApprove(inv.id)} disabled={processing}><Check className="h-4 w-4 mr-1" />Approve</Button>
                    <Button size="sm" variant="destructive" onClick={() => onReject(inv)} disabled={processing}><XCircle className="h-4 w-4 mr-1" />Reject</Button>
                  </div>
                </div>
              );
            })}
            {pendingReview.length > 3 && (
              <p className="text-sm text-purple-600 text-center">+ {pendingReview.length - 3} more pending adjustments</p>
            )}
          </div>
        </CardContent>
      </Card>
    )}

    {/* Bulk Action Bar */}
    {selectedIds.size > 0 && (
      <Card className="border-amber-300 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <SquareCheck className="h-5 w-5 text-amber-700" />
              <span className="font-medium text-amber-800">{selectedIds.size} invoice{selectedIds.size > 1 ? 's' : ''} selected</span>
            </div>
            <div className="flex flex-wrap gap-2 ml-auto">
              <Button size="sm" variant="outline" className="bg-white" onClick={onOpenBulkPay} disabled={!canBulkPay} data-testid="bulk-mark-paid-btn">
                <DollarSign className="h-4 w-4 mr-1" />Mark Paid
              </Button>
              <Button size="sm" variant="outline" className="bg-white" onClick={onOpenReminder} disabled={!canBulkRemind} data-testid="bulk-send-reminder-btn">
                <Send className="h-4 w-4 mr-1" />Send Reminder
              </Button>
              <Button size="sm" variant="outline" className="bg-white text-gray-700" onClick={onOpenCancel} disabled={!canBulkCancel} data-testid="bulk-cancel-btn">
                <Ban className="h-4 w-4 mr-1" />Cancel
              </Button>
              <Button size="sm" variant="outline" className="bg-white text-red-700 border-red-300 hover:bg-red-50" onClick={onOpenVoidBulk} disabled={!canBulkVoid} data-testid="bulk-void-btn">
                <FileX className="h-4 w-4 mr-1" />Void
              </Button>
              <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="clear-selection-btn">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    )}

    {/* Filters */}
    <div className="flex flex-col sm:flex-row gap-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input placeholder="Search invoices, clients..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
      </div>
      <Select value={clientFilter} onValueChange={setClientFilter}>
        <SelectTrigger className="w-full sm:w-56" data-testid="client-filter-select"><SelectValue placeholder="All Clients" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Clients</SelectItem>
          {clientOptions.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-full sm:w-48"><Filter className="h-4 w-4 mr-2" /><SelectValue placeholder="Status" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          {Object.entries(statusConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>

    {/* Invoice Table */}
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-3 w-10">
                  <button onClick={toggleSelectAll} className="flex items-center justify-center" data-testid="select-all-checkbox">
                    {selectedIds.size === 0 ? (
                      <Square className="h-5 w-5 text-gray-400" />
                    ) : selectedIds.size === filteredInvoices.length ? (
                      <SquareCheck className="h-5 w-5 text-amber-600" />
                    ) : (
                      <MinusSquare className="h-5 w-5 text-amber-600" />
                    )}
                  </button>
                </th>
                <th className="text-left p-3 font-medium text-gray-600">Invoice</th>
                <th className="text-left p-3 font-medium text-gray-600">Client</th>
                <th className="text-left p-3 font-medium text-gray-600">Status</th>
                <th className="text-right p-3 font-medium text-gray-600">Amount</th>
                <th className="text-left p-3 font-medium text-gray-600">Due Date</th>
                <th className="text-center p-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredInvoices.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-500">No invoices found</td></tr>
              ) : filteredInvoices.map(inv => {
                const es = getEffectiveStatus(inv);
                const sc = statusConfig[es] || statusConfig.open;
                const isSelected = selectedIds.has(inv.id);
                return (
                  <tr key={inv.id} className={`hover:bg-gray-50 ${inv.status === 'amend_for_review' ? 'bg-purple-50' : ''} ${isSelected ? 'bg-amber-50' : ''}`}>
                    <td className="p-3">
                      <button onClick={() => toggleSelect(inv.id)} className="flex items-center justify-center" data-testid={`select-invoice-${inv.id}`}>
                        {isSelected ? (
                          <SquareCheck className="h-5 w-5 text-amber-600" />
                        ) : (
                          <Square className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </td>
                    <td className="p-3">
                      <p className="font-medium">{inv.invoice_number}</p>
                      <p className="text-xs text-gray-500">{inv.order_number}</p>
                    </td>
                    <td className="p-3">
                      <p>{inv.client_name || '—'}</p>
                      {inv.client_type_name && <p className="text-xs text-gray-500">{inv.client_type_name}</p>}
                      <LocationLine
                        name={inv.location_name}
                        source={inv.location_name_source}
                        clientName={inv.client_name}
                        testId={`invoice-row-location-${inv.id}`}
                      />
                    </td>
                    <td className="p-3">
                      <Badge className={sc.color}>{sc.label}</Badge>
                      {inv.has_pending_adjustment && <Badge className="bg-purple-100 text-purple-800 ml-1 text-xs">Needs Review</Badge>}
                      {inv.credit_memo_number && <Badge className="bg-gray-100 text-gray-600 ml-1 text-xs">CM</Badge>}
                    </td>
                    <td className="p-3 text-right">
                      <p className="font-medium">${(inv.amount || 0).toFixed(2)}</p>
                      {inv.amount_paid > 0 && inv.status !== 'paid' && (
                        <p className="text-xs text-green-600">Paid: ${(inv.amount_paid || 0).toFixed(2)}</p>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={es === 'overdue' ? 'text-red-600 font-medium' : ''}>{new Date(inv.due_date).toLocaleDateString()}</span>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex gap-1 justify-center">
                        <Button size="sm" variant="ghost" onClick={() => onViewDetail(inv)} data-testid={`view-invoice-${inv.id}`}><Eye className="h-4 w-4" /></Button>
                        {!['paid', 'cancelled', 'void'].includes(inv.status) && (
                          <Button size="sm" variant="ghost" className="text-green-700" onClick={() => onApplyPayment(inv)} data-testid={`apply-payment-${inv.id}`} title="Apply Manual Payment"><Banknote className="h-4 w-4" /></Button>
                        )}
                        {inv.status === 'amend_for_review' && (
                          <>
                            <Button size="sm" variant="ghost" className="text-green-600" onClick={() => onApprove(inv.id)} disabled={processing}><Check className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onReject(inv)} disabled={processing}><XCircle className="h-4 w-4" /></Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  </>
);
